import { Badge, Button, Card } from "@design";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import type { DuplicatePair, MergePage } from "@/api";
import { Text } from "@/components/Text";
import { formatList } from "@/i18n/format";
import { isNodeType } from "../nodeTypes";
import { VisibilityBadge } from "../VisibilityBadge";
import { wikiFileHref } from "../wikilinks";

function PagePanel({ page, spaceUuid }: Readonly<{ page: MergePage; spaceUuid: string }>) {
  const { t } = useTranslation("knowledge");
  return (
    <section
      aria-label={page.title}
      className="flex min-w-0 flex-col gap-2 rounded-lg border border-border-default p-3"
    >
      <Link to={wikiFileHref(spaceUuid, page.path)} className="underline-offset-2 hover:underline">
        <Text as="span" variant="h3">
          {page.title}
        </Text>
      </Link>
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant="neutral">{t(`type.${isNodeType(page.nodeType) ? page.nodeType : "none"}`)}</Badge>
        <VisibilityBadge visibility={page.visibility} />
      </div>
      <Text variant="caption" tone="tertiary" className="break-all">
        {page.path}
      </Text>
      {page.aliases.length > 0 && (
        <Text variant="body-sm" tone="secondary">
          {t("duplicates.aliases", { names: formatList(page.aliases) })}
        </Text>
      )}
      {page.summary && (
        <Text variant="body-sm" tone="secondary">
          {page.summary}
        </Text>
      )}
    </section>
  );
}

/** Two pages that may be the same thing, side by side, with the three choices. */
export function DuplicatePairCard({
  pair,
  spaceUuid,
  onMerge,
  onDismiss,
  onSkip,
  dismissing,
}: Readonly<{
  pair: DuplicatePair;
  spaceUuid: string;
  onMerge: () => void;
  onDismiss: () => void;
  onSkip: () => void;
  dismissing: boolean;
}>) {
  const { t } = useTranslation("knowledge");
  return (
    <Card>
      <article
        aria-label={t("duplicates.pairLabel", { a: pair.a.title, b: pair.b.title })}
        className="flex flex-col gap-3"
      >
        <Text variant="body-sm" tone="secondary">
          {t("duplicates.why", { reason: t(`reason.${pair.reason}`) })}
        </Text>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <PagePanel page={pair.a} spaceUuid={spaceUuid} />
          <PagePanel page={pair.b} spaceUuid={spaceUuid} />
        </div>
        <div className="flex flex-wrap justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onSkip}>
            {t("duplicates.skip")}
          </Button>
          <Button variant="secondary" size="sm" loading={dismissing} onClick={onDismiss}>
            {t("duplicates.notTheSame")}
          </Button>
          <Button variant="primary" size="sm" onClick={onMerge}>
            {t("duplicates.merge")}
          </Button>
        </div>
      </article>
    </Card>
  );
}
